import { Link } from 'react-router-dom';
import Logo from '../../components/Logo';
import { colors } from '../../theme';

/**
 * The privacy policy, at /privacy.
 *
 * Google Play will not review an app without a policy on a public page, and
 * it has to be reachable without signing in - so this route sits outside the
 * guards, and says what the code actually does rather than boilerplate.
 *
 * If the app starts collecting something new, this page changes in the same
 * commit. The Data safety form in Play Console is answered from it.
 */

const UPDATED = '10 October 2026';

const COLLECTED = [
  ['Your mobile number', 'It is how you sign in, and how the shop or driver reaches you about an order.', 'Shared with the business fulfilling your order, and with the driver delivering it.'],
  ['Your name', 'So the shop knows who to hand the order to.', 'Same.'],
  ['Delivery addresses', 'So an order can arrive.', 'Shared with the business and the delivery rider for that order.'],
  ['Your location', 'To show businesses near you, and to track a ride while it is happening.', 'Your live location during a ride is shared with that driver, and only while the ride is running.'],
  ['Photos you upload', 'Product photos if you sell on KAAMWALA, and anything you attach to a support request.', 'Product photos are public on your shop page. Support attachments go only to our support team.'],
  ['Orders, bookings and rides', 'To show you your history, and so a business can run its accounts.', 'Shared with the business you ordered from.'],
  ['Device notification token', 'To tell you your order is on its way.', 'Not shared.'],
];

const NOT_COLLECTED = [
  'Card numbers, UPI PINs or bank details. Payments go through Razorpay; the money details are typed on their screen and KAAMWALA never receives or stores them.',
  'Your contacts, your photo gallery beyond the images you pick, your calls or your messages.',
  'Any tracking for advertising. KAAMWALA does not sell data and does not carry third-party ad networks.',
];

const Section = ({ title, children }) => (
  <section style={{ marginBottom: 34 }}>
    <h2 style={{
      fontSize: 19, fontWeight: 800, color: colors.ink, marginBottom: 10,
    }}
    >
      {title}
    </h2>
    <div style={{ fontSize: 15, lineHeight: 1.75, color: colors.muted }}>{children}</div>
  </section>
);

const PrivacyPage = () => (
  <div style={{ background: colors.bg, minHeight: '100vh' }}>
    <header style={{
      borderBottom: `1px solid ${colors.line}`,
      background: colors.card,
      padding: '14px 20px',
    }}
    >
      <div style={{
        maxWidth: 820, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 12,
      }}
      >
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
          <Logo size={34} />
          <span style={{ fontWeight: 900, color: colors.ink, fontSize: 16 }}>KAAMWALA</span>
        </Link>
      </div>
    </header>

    <main style={{ maxWidth: 820, margin: '0 auto', padding: '36px 20px 80px' }}>
      <h1 style={{ fontSize: 30, fontWeight: 900, color: colors.ink, marginBottom: 6 }}>
        Privacy Policy
      </h1>
      <p style={{ color: colors.muted, fontSize: 14, marginBottom: 30 }}>
        Last updated {UPDATED} · KAAMWALA, operated by STACKERIO, India
      </p>

      <Section title="The short version">
        KAAMWALA connects you with shops, service providers and drivers near you. To do that we
        need your phone number, your name and where to deliver. We pass those to the business
        serving you, because they cannot serve you otherwise. We do not sell your data, we do not
        run ad networks, and we never see your card details. You can delete your account and your
        personal data yourself, at any time, from
        {' '}
        <Link to="/deleteaccount" style={{ color: colors.primary, fontWeight: 700 }}>
          getkaamwala.com/deleteaccount
        </Link>
        .
      </Section>

      <Section title="What we collect, and why">
        <div style={{ display: 'grid', gap: 14, marginTop: 6 }}>
          {COLLECTED.map(([what, why, shared]) => (
            <div
              key={what}
              style={{
                background: colors.card,
                border: `1px solid ${colors.line}`,
                borderRadius: 14,
                padding: '14px 16px',
              }}
            >
              <div style={{ fontWeight: 800, color: colors.ink, marginBottom: 4 }}>{what}</div>
              <div style={{ fontSize: 14.5 }}>{why}</div>
              <div style={{ fontSize: 13.5, marginTop: 6, color: colors.primary }}>{shared}</div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="What we do not collect">
        <ul style={{ paddingLeft: 20, display: 'grid', gap: 8 }}>
          {NOT_COLLECTED.map((line) => <li key={line}>{line}</li>)}
        </ul>
      </Section>

      <Section title="Location, specifically">
        The app asks for location so it can list what is actually near you, fill in your address
        when you ask it to, and show a driver moving towards you on the map. You can refuse: the
        app still works, and you type the address instead. Location is not collected in the
        background when the app is closed.
      </Section>

      <Section title="Who else sees your data">
        <p style={{ marginBottom: 10 }}>
          The business you order from, and only what they need to serve you. Beyond that:
        </p>
        <ul style={{ paddingLeft: 20, display: 'grid', gap: 8 }}>
          <li>
            <b>Razorpay</b> handles online payments. They receive the amount and your contact
            details to process it, under their own privacy policy.
          </li>
          <li>
            <b>Google Maps</b> is used for maps, distances and travel times.
          </li>
          <li>
            <b>Amazon Web Services</b> stores the images uploaded to the app, in Mumbai.
          </li>
          <li>
            <b>WhatsApp</b> delivers your sign-in code.
          </li>
          <li>
            Government or police, where the law actually requires it of us.
          </li>
        </ul>
        <p style={{ marginTop: 10 }}>
          Nobody else. We do not sell, rent or trade personal data.
        </p>
      </Section>

      <Section title="How long we keep it">
        Your account and everything personal on it lasts until you delete it. When you do, it goes
        the same day. Orders, bookings, rides and payments are kept afterwards because a business
        has to account for what it sold and a tax return has to add up - but every personal field
        on them is overwritten first, so what is left records that a sale happened, for how much
        and on what day, and not who made it.
      </Section>

      <Section title="Deleting your account">
        <p style={{ marginBottom: 10 }}>Two ways, both yours to use without asking us:</p>
        <ul style={{ paddingLeft: 20, display: 'grid', gap: 8 }}>
          <li>In the app: Profile → Delete account.</li>
          <li>
            On the web, with no app installed:
            {' '}
            <Link to="/deleteaccount" style={{ color: colors.primary, fontWeight: 700 }}>
              getkaamwala.com/deleteaccount
            </Link>
            . Enter your number, confirm the code we send, and it is done.
          </li>
        </ul>
        <p style={{ marginTop: 10 }}>
          Deleted means deleted: your account, addresses, basket, reviews, support history,
          anything you listed for sale, and your work profile. There is no waiting period and no
          undo.
        </p>
      </Section>

      <Section title="Children">
        KAAMWALA is not for under-13s and we do not knowingly hold data about them. If you believe
        a child has an account, write to us and it will be removed.
      </Section>

      <Section title="Security">
        Everything between your phone and our servers travels over HTTPS. Payment keys and other
        secrets are stored encrypted. Access to live data is limited to the people who need it to
        run the service. No system is perfect, and we will not pretend otherwise - if something
        goes wrong that affects you, we will tell you.
      </Section>

      <Section title="Changes">
        If this policy changes, the date at the top changes with it, and anything significant is
        announced in the app before it takes effect.
      </Section>

      <Section title="Contact">
        <div style={{
          background: colors.card,
          border: `1px solid ${colors.line}`,
          borderRadius: 14,
          padding: '14px 16px',
        }}
        >
          <div>
            Email:
            {' '}
            <a href="mailto:support@kaamwala.com" style={{ color: colors.primary, fontWeight: 700 }}>
              support@kaamwala.com
            </a>
          </div>
          <div style={{ marginTop: 6 }}>Operated by STACKERIO, Yamunanagar, Haryana, India</div>
        </div>
      </Section>
    </main>
  </div>
);

export default PrivacyPage;
